import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { TWO_MINUTES_MS } from '@app/src/wf-constants'

type UseProvidersWithExistingInquiriesParams = {
  templateIds: number[]
  periodName?: string | null
  enabled?: boolean
}

type UseProvidersWithExistingInquiriesResult = {
  providerIdsWithExistingInquiry: Set<number>
  isLoading: boolean
}

/**
 * Returns the set of provider ids that already have a (non-deleted) inquiry for
 * any of the given questionnaire templates in the selected reporting period.
 */
export const useProvidersWithExistingInquiries = ({
  templateIds,
  periodName,
  enabled = true,
}: UseProvidersWithExistingInquiriesParams): UseProvidersWithExistingInquiriesResult => {
  const isEnabled = enabled && templateIds.length > 0 && Boolean(periodName)

  const {
    facets: [providerFacets = []],
    isLoading,
  } = useFetchFacets({
    key: FetchKey.InquiryByProvider,
    endpoint: endpoints.inquiryWithFacets,
    facetsParam: [{ name: 'providerId', isEnum: true }],
    shouldShowErrorNotification: false,
    filter: [
      {
        name: 'questionnaireTemplateId',
        filters: [{ value: templateIds, operator: Operators.In }],
      },
      {
        name: 'periodName',
        filters: [{ value: periodName ?? '', operator: Operators.EqualTo }],
      },
      {
        name: 'deletedAt',
        filters: [{ operator: Operators.IsNull }],
      },
    ],
    options: { enabled: isEnabled, staleTime: TWO_MINUTES_MS },
  })

  const providerIdsWithExistingInquiry = new Set(providerFacets.map(facet => Number(facet.value)))

  return { providerIdsWithExistingInquiry, isLoading }
}
